import { ReactElement } from 'react';
import { Link, Tooltip } from '@mui/material';
import {
  AwsClientOverrides,
  buildRoute,
  useAwsGetter,
  getLatestWebRevision,
  resolveWebFunctionLogGroup,
} from '@localstack/services';
import { NavLink } from '@localstack/ui';

import { LambdaRoutes } from '../types';

interface WebFunctionTabsProps {
  routes: LambdaRoutes;
  functionName?: string;
  clientOverrides?: AwsClientOverrides;
}

export const WebFunctionTabs = ({ routes, functionName, clientOverrides }: WebFunctionTabsProps): ReactElement => {
  const { data: revisions } = useAwsGetter(
    'LambdaWeb',
    'listWebFunctionRevisions',
    [{ functionName: functionName ?? '' }],
    {
      clientOverrides,
      enable: !!functionName,
      pagination: { inputToken: 'nextToken', outputToken: 'nextToken', itemsKey: 'revisions' },
    },
  );
  const latestRevisionId = getLatestWebRevision(revisions?.revisions)?.revisionId;
  const { data: revision } = useAwsGetter(
    'LambdaWeb',
    'getWebFunctionRevision',
    [{ functionName: functionName ?? '', revisionId: latestRevisionId ?? '' }],
    { clientOverrides, enable: !!functionName && !!latestRevisionId },
  );
  const configuredGroup = revision?.serviceConfig?.telemetryConfig?.loggingConfig?.logGroup;
  const { data: groups } = useAwsGetter(
    'CloudWatchLogs',
    'describeLogGroups',
    [{ logGroupNamePrefix: configuredGroup ?? '' }],
    { clientOverrides, enable: !!configuredGroup, swrOverrides: { refreshInterval: 5000 } },
  );
  const logGroupName = resolveWebFunctionLogGroup(configuredGroup, groups?.logGroups);

  return (
    <>
      <NavLink to={buildRoute(routes.RESOURCES_LAMBDA_WEB_FUNCTION, { name: functionName })} end>
        Details
      </NavLink>
      <NavLink to={buildRoute(routes.RESOURCES_LAMBDA_WEB_REVISIONS, { name: functionName })}>Revisions</NavLink>
      <NavLink to={buildRoute(routes.RESOURCES_LAMBDA_WEB_ENDPOINTS, { name: functionName })}>Endpoints</NavLink>
      {logGroupName ? (
        <NavLink
          target="_blank"
          rel="noopener noreferrer"
          to={buildRoute(routes.RESOURCES_CLOUDWATCH_GROUP_EVENTS, { logGroupName })}
          end
        >
          Logs
        </NavLink>
      ) : (
        <Tooltip title="Logs will be available after the function produces logs.">
          <Link component="span" underline="none" color="text.disabled" aria-disabled="true">
            Logs
          </Link>
        </Tooltip>
      )}
    </>
  );
};
